import React, { createContext, useContext, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_CUSTOMER_WISHLIST, ADD_PRODUCTS_TO_WISHLIST, REMOVE_PRODUCTS_FROM_WISHLIST } from '../queries/customerQueries';
import { useAuth } from './AuthContext';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();

  const { data, loading, refetch } = useQuery(GET_CUSTOMER_WISHLIST, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network'
  });

  const [addProductsToWishlistMutation] = useMutation(ADD_PRODUCTS_TO_WISHLIST);
  const [removeProductsFromWishlistMutation] = useMutation(REMOVE_PRODUCTS_FROM_WISHLIST);

  const wishlist = data?.customer?.wishlists?.[0];
  const wishlistId = wishlist?.id;
  const wishlistItems = wishlist?.items_v2?.items || [];

  const isInWishlist = useCallback((sku) => {
    return wishlistItems.some(item => item.product.sku === sku);
  }, [wishlistItems]);

  const addToWishlist = useCallback(async (sku, quantity = 1) => {
    if (!isAuthenticated) {
      throw new Error('Please log in to add items to your wishlist');
    }

    if (!wishlistId) {
      throw new Error('Wishlist not found');
    }

    try {
      await addProductsToWishlistMutation({
        variables: {
          wishlistId,
          wishlistItems: [{
            sku,
            quantity
          }]
        }
      });

      await refetch();
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      throw error;
    }
  }, [isAuthenticated, wishlistId, addProductsToWishlistMutation, refetch]);

  const removeFromWishlist = useCallback(async (wishlistItemId) => {
    if (!isAuthenticated || !wishlistId) {
      return;
    }

    try {
      await removeProductsFromWishlistMutation({
        variables: {
          wishlistId,
          wishlistItemsIds: [wishlistItemId]
        }
      });

      await refetch();
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      throw error;
    }
  }, [isAuthenticated, wishlistId, removeProductsFromWishlistMutation, refetch]);

  const getWishlistItemId = useCallback((sku) => {
    const item = wishlistItems.find(item => item.product.sku === sku);
    return item?.id;
  }, [wishlistItems]);

  const value = {
    wishlist,
    wishlistItems,
    loading,
    isInWishlist,
    addToWishlist,
    removeFromWishlist,
    getWishlistItemId,
    refetch
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
